import { AlertTriangle, ArrowRight } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { SeverityBadge } from './badges';
import { ErrorEventSeverity, SeverityGroup } from '../../../services/api';

/** Eine Zeile der Folgen-Vorschau: wie ein Identifier vor und nach dem Löschen eingestuft würde. */
export interface SeverityDeletionImpact {
  errorIdentifier: string;
  before: ErrorEventSeverity;
  after: ErrorEventSeverity;
  /** `true`, wenn eine eigene Identifier-Regel die Gruppen-Severity ohnehin sticht. */
  unchanged: boolean;
}

export interface SeverityGroupDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: SeverityGroup | null;
  /** Vollständige Folgen-Liste; der Dialog zeigt die ersten Einträge und zählt den Rest. */
  impacts: SeverityDeletionImpact[];
  /** Anzahl der Kunden-Overrides, die mit der Gruppe verschwinden. */
  customerOverrideCount: number;
  submitting: boolean;
  onConfirm: () => Promise<void>;
}

/** Wie viele Folgen-Zeilen der Dialog ausschreibt, bevor er zusammenfasst. */
const IMPACT_PREVIEW_LIMIT = 4;

/**
 * Löschbestätigung mit Folgen-Vorschau.
 *
 * Zeigt bewusst die konkreten Einstufungs-Änderungen statt einer generischen
 * „kann nicht rückgängig gemacht werden"-Warnung — das ist die Information,
 * die die Entscheidung trägt.
 */
export function SeverityGroupDeleteDialog({
  open,
  onOpenChange,
  group,
  impacts,
  customerOverrideCount,
  submitting,
  onConfirm,
}: SeverityGroupDeleteDialogProps) {
  if (!group) return null;

  const shown = impacts.slice(0, IMPACT_PREVIEW_LIMIT);
  const remaining = impacts.length - shown.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Gruppe „{group.name}" löschen?</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {group.memberCount === 0
              ? 'Der Gruppe ist kein Identifier zugeordnet.'
              : `Die ${group.memberCount} zugeordneten Identifier verlieren ihre Gruppe und fallen auf die nächste greifende Regel zurück.`}{' '}
            Bereits erfasste Events bleiben unverändert.
          </p>

          {customerOverrideCount > 0 && (
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
              <span>
                {customerOverrideCount} Kunden-Override
                {customerOverrideCount === 1 ? '' : 's'} auf diese Gruppe {customerOverrideCount === 1 ? 'wird' : 'werden'}{' '}
                mit gelöscht.
              </span>
            </div>
          )}

          {shown.length > 0 && (
            <div className="rounded-lg border border-gray-200 p-3">
              <div className="mb-2 text-xs font-medium text-gray-700">Auswirkung auf neue Events</div>
              <div className="space-y-1.5">
                {shown.map((impact) => (
                  <div key={impact.errorIdentifier} className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="font-mono text-gray-700">{impact.errorIdentifier}</span>
                    <SeverityBadge severity={impact.before} />
                    <ArrowRight className="h-3 w-3 text-gray-400" />
                    <SeverityBadge severity={impact.after} />
                    {impact.unchanged && <span className="text-gray-400">(eigene Severity bleibt)</span>}
                  </div>
                ))}
              </div>
              {remaining > 0 && <div className="mt-2 text-xs text-gray-400">… {remaining} weitere</div>}
            </div>
          )}
        </div>

        <DialogFooter className="sm:justify-between">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Abbrechen
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={submitting}>
            Gruppe löschen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
